/**
 * Validation utilities for business and customer profiles, payment links, and builders.
 */

/**
 * Validates if the given string is a valid email address.
 * Empty email is considered invalid unless checked separately.
 */
export function isValidEmail(email: string): boolean {
  if (!email) return false;
  // A standard, robust email regex pattern
  const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
  return emailRegex.test(email);
}

/**
 * Validates if the given string is a valid phone/mobile number.
 * Empty phone is allowed (since it's optional).
 * Allows optional leading +, numbers, spaces, parentheses, hyphens, min 7 digits, max 20.
 */
export function isValidPhone(phone: string): boolean {
  if (!phone) return true; // Optional field
  // Allow digits, space, hyphen, parentheses, and leading plus.
  // Must contain at least 7 digits to prevent spam/accidental input.
  const phoneRegex = /^\+?[0-9\s\-()]{7,20}$/;
  // Ensure there are at least 7 digits in the string
  const digitCount = (phone.match(/\d/g) || []).length;
  return phoneRegex.test(phone) && digitCount >= 7;
}

/**
 * Validates Stripe payment links format.
 * Prevents harmful/phishing links.
 * Empty url is allowed (optional).
 */
export function isValidStripeLink(url: string): boolean {
  if (!url) return true; // Optional field
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;
    
    const host = parsed.hostname.toLowerCase();
    // Validate that it belongs to verified stripe.com subdomains or stripe.com itself.
    // E.g., buy.stripe.com, billing.stripe.com, checkout.stripe.com, etc.
    return host === 'buy.stripe.com' || host === 'billing.stripe.com' || host === 'checkout.stripe.com' || host.endsWith('.stripe.com');
  } catch {
    return false;
  }
}

/**
 * Validates PayPal payment links format.
 * Prevents harmful/phishing links.
 * Empty url is allowed (optional).
 */
export function isValidPaypalLink(url: string): boolean {
  if (!url) return true; // Optional field
  try {
    const parsed = new URL(url);
    if (parsed.protocol !== 'https:') return false;

    const host = parsed.hostname.toLowerCase();
    // Validate that it belongs to verified paypal.me / paypal.com subdomains.
    return host === 'paypal.me' || host === 'www.paypal.com' || host === 'paypal.com' || host.endsWith('.paypal.com') || host.endsWith('.paypal.me');
  } catch {
    return false;
  }
}
